import { isEmpty } from 'ramda';
import React, { useEffect, useMemo, useState } from 'react';
import { Redirect, RouteComponentProps } from 'react-router';

import { useAuth } from '../../../app/AuthorizationProvider';
import { LoadingOverlay } from '../../../components/LoadingOverlay';
import {
  Agent,
  useCreateScheduleMutation,
  usePutScheduleToProductionMutation,
  useRemoveScheduleFromProductionMutation,
  useUpdateScheduleMutation,
  useCreateAutoScheduleJobMutation,
  useGetAgentsQuery,
  useGetScheduleElementsQuery,
  useGetQueuesQuery,
  useGetSkillsAllQuery,
  useGetTimezonesAllQuery,
  useGetScheduleQuery,
  useGetSupervisedAgentsQuery,
} from '../../../graphql/components';
import { CreateEditView, ActionRouteType, IdRouteType, Values } from '../../../types';
import { dateToStamp } from '../../../utils/date';
import { createEndOfDateRange } from '../../../utils/dateRange';
import { LocationState } from '../types';
import { transformInitialStaffingPerQueueData } from '../utils/mapChartData';
import { getPathname } from '../utils/pathname';
import { cloneSchedule } from '../utils/transformScheduleItems';
import { SchedulingComposer } from './SchedulingComposer';

export type SchedulingComposerSceneProps = RouteComponentProps<
  ActionRouteType & Partial<IdRouteType>,
  {},
  LocationState
>;

const RefetchState = {
  completed: 'COMPLETED',
  inProgress: 'IN_PROGRESS',
  waiting: 'WAITING',
} as const;

export const SchedulingComposerScene: React.FC<SchedulingComposerSceneProps> = ({ location, match }) => {
  const { localeObj, userId } = useAuth();

  const { action, id = '' } = match.params;
  const { state } = location;
  const shouldEarlyLeave = !(state && state.formValues && state.scheduleState); // Redirect to first step when missing location.state data

  const isCreate = action === CreateEditView.CREATE;
  const isView = action === CreateEditView.VIEW;

  // Control variable to trigger schedule refetch during automatic action VIEW/EDIT redirect when autoSchedule job is finished
  const [shouldRefetchSchedule, setShouldRefetchSchedule] = useState<Values<typeof RefetchState>>(
    state?.forceRefetchSchedule ? RefetchState.inProgress : RefetchState.waiting
  );

  useEffect(() => {
    setShouldRefetchSchedule(state?.forceRefetchSchedule ? RefetchState.inProgress : RefetchState.waiting);
  }, [state?.forceRefetchSchedule]);

  const { formValues, oldFormValues, scheduleState } = state ?? {};
  const { cloneScheduleId, queueIds, schedulingEndDateTime, schedulingStartDateTime, timezone } = formValues ?? {};
  const {
    autoScheduleOptions,
    autoScheduleSelectedRange,
    isDirty,
    initialStaffingPerQueue,
    schedule: scheduleFromHistory,
    scheduledAgents,
    scheduleDaysSettings,
    usedScheduleElements,
    viewedTimezone,
    usePrecalculatedQueuesStaffing = true,
  } = scheduleState ?? {};

  const startDateTime = schedulingStartDateTime ?? scheduleFromHistory?.info?.scheduleRange?.startDateTime;
  const endDateTime =
    (schedulingEndDateTime && createEndOfDateRange(schedulingEndDateTime)) ??
    scheduleFromHistory?.info?.scheduleRange?.endDateTime;
  const shouldClone = cloneSchedule(formValues ?? {}, oldFormValues);
  const composerElements = shouldClone ? undefined : usedScheduleElements;
  const composerAgents = shouldClone ? undefined : scheduledAgents;

  const [createSchedule, { data: createScheduleData, loading: isCreateScheduleInProgress }] =
    useCreateScheduleMutation();

  const createdScheduleId = createScheduleData?.createSchedule?.id;

  const [putScheduleToProduction, { loading: isPutScheduleToProductionInProgress }] =
    usePutScheduleToProductionMutation();

  const [removeScheduleFromProduction, { loading: isRemoveScheduleFromProductionInProgress }] =
    useRemoveScheduleFromProductionMutation();

  const [updateSchedule, { loading: isUpdateScheduleInProgress }] = useUpdateScheduleMutation();

  const [createAutoScheduleJob, { loading: isCreateAutoScheduleJobInProgress }] = useCreateAutoScheduleJobMutation();

  const {
    data: agentsData,
    loading: isGetAgentsInProgress,
    error: getAgentsError,
  } = useGetAgentsQuery({
    fetchPolicy: 'network-only',
    variables: {
      queueIds: queueIds?.toString(),
      includeInactive: true,
    },
    skip: shouldEarlyLeave,
  });

  const {
    data: supervisedAgentsData,
    loading: isGetSupervisedAgentsInProgress,
    error: getSupervisedAgentsError,
  } = useGetSupervisedAgentsQuery({
    fetchPolicy: 'network-only',
    variables: {
      supervisorId: userId,
      filters: {
        queueIds: queueIds?.toString(),
        includeInactive: true,
      },
    },
    skip: shouldEarlyLeave,
  });

  const {
    data: scheduleElementsData,
    loading: isGetScheduleElementsInProgress,
    error: getScheduleElementsError,
  } = useGetScheduleElementsQuery({
    fetchPolicy: 'network-only',
  });

  const {
    data: queuesData,
    loading: isGetQueuesInProgress,
    error: getQueuesError,
  } = useGetQueuesQuery({
    fetchPolicy: 'network-only',
    skip: shouldEarlyLeave,
  });

  const {
    data: skillsData,
    loading: isGetSkillsAllInProgress,
    error: getSkillsAllError,
  } = useGetSkillsAllQuery({
    fetchPolicy: 'network-only',
    skip: shouldEarlyLeave,
  });

  const {
    data: timezonesData,
    loading: isGetTimezonesAllInProgress,
    error: getTimezonesAllError,
  } = useGetTimezonesAllQuery({ skip: shouldEarlyLeave });

  const {
    data: scheduleData,
    loading: isGetScheduleInProgress,
    error: getScheduleError,
    refetch: refetchGetSchedule,
  } = useGetScheduleQuery({
    fetchPolicy: 'network-only',
    skip:
      shouldEarlyLeave ||
      (isCreate && !shouldClone) ||
      (!!composerElements && shouldRefetchSchedule !== RefetchState.completed) ||
      (!!createdScheduleId && shouldRefetchSchedule === RefetchState.waiting),
    variables: { id: isCreate && shouldClone ? cloneScheduleId! : id },
    notifyOnNetworkStatusChange: true,
  });

  if (shouldRefetchSchedule === RefetchState.inProgress) {
    refetchGetSchedule({
      id: isCreate && shouldClone ? cloneScheduleId! : id,
    });

    setShouldRefetchSchedule(RefetchState.completed);
  }

  const isLoadingData =
    isGetAgentsInProgress ||
    isGetSupervisedAgentsInProgress ||
    isGetQueuesInProgress ||
    isGetScheduleElementsInProgress ||
    isGetScheduleInProgress ||
    isGetSkillsAllInProgress ||
    isGetTimezonesAllInProgress;

  const isMutationInProgress =
    isCreateAutoScheduleJobInProgress ||
    isCreateScheduleInProgress ||
    isPutScheduleToProductionInProgress ||
    isRemoveScheduleFromProductionInProgress ||
    isUpdateScheduleInProgress;

  const hasResultedWithError =
    !!getAgentsError ||
    !!getSupervisedAgentsError ||
    !!getQueuesError ||
    !!getScheduleElementsError ||
    !!getScheduleError ||
    !!getSkillsAllError ||
    !!getTimezonesAllError;

  const agents: Agent[] = agentsData?.agents?.agents ?? [];
  const supervisedAgents: Agent[] = supervisedAgentsData?.supervisedAgents?.agents ?? [];
  const allTimezones = timezonesData?.timezones ?? [];
  const queues = queuesData?.queues ?? [];
  const schedule = scheduleData?.schedule ?? scheduleFromHistory;
  const scheduleElements = scheduleElementsData?.scheduleElements ?? [];
  const skills = skillsData?.skills ?? [];

  const composerInitialStaffingPerQueues = useMemo(
    () => initialStaffingPerQueue ?? transformInitialStaffingPerQueueData(scheduleData?.schedule?.scheduleQueues),
    [initialStaffingPerQueue, scheduleData?.schedule?.scheduleQueues]
  );

  const skipRendering =
    (isLoadingData && shouldRefetchSchedule === RefetchState.waiting) ||
    hasResultedWithError ||
    isEmpty(scheduleElementsData) ||
    (!isCreate && isEmpty(scheduleData));

  if (shouldEarlyLeave) {
    return <Redirect to={getPathname(action, id)} />;
  }

  return (
    <LoadingOverlay loading={isLoadingData || isMutationInProgress}>
      <SchedulingComposer
        agents={agents}
        supervisedAgents={supervisedAgents}
        allTimezones={allTimezones}
        cloneScheduleId={cloneScheduleId}
        createAutoScheduleJob={createAutoScheduleJob}
        createdScheduleId={createdScheduleId}
        createSchedule={createSchedule}
        endDateTime={dateToStamp(endDateTime, localeObj)!}
        initialStaffingPerQueue={composerInitialStaffingPerQueues}
        isCreate={isCreate}
        isDirty={!!isDirty}
        isEdit={action === CreateEditView.EDIT}
        isLoading={isLoadingData}
        isView={isView}
        lastAutoScheduleOptions={autoScheduleOptions}
        lastAutoScheduleSelectedRange={autoScheduleSelectedRange}
        lastViewedTimezone={viewedTimezone}
        putScheduleToProduction={putScheduleToProduction}
        queueIds={queueIds!}
        queues={queues}
        removeScheduleFromProduction={removeScheduleFromProduction}
        schedule={schedule}
        scheduledAgents={composerAgents}
        scheduleDaysSettings={scheduleDaysSettings}
        scheduleElements={scheduleElements}
        skills={skills}
        skipRendering={skipRendering}
        startDateTime={dateToStamp(startDateTime, localeObj)!}
        timezone={timezone!}
        updateSchedule={updateSchedule}
        usedScheduleElements={composerElements}
        usePrecalculatedQueuesStaffing={usePrecalculatedQueuesStaffing}
        viewType={action}
      />
    </LoadingOverlay>
  );
};
